/**
 * Returns the base URL for API calls.
 * Uses same-origin relative URLs to avoid CORS issues when the app
 * is served from a custom domain (e.g. personalplants.online) while
 * REACT_APP_BACKEND_URL points to the Emergent host.
 * The /api prefix routing works in all environments (preview, deployed, custom domain).
 */
export const API_BASE_URL = '';

// Global axios interceptor for offline behaviour (iter 94 → iter 98).
//
// Reads (GET) always pass through and fall back to the IndexedDB mirror
// in usePlantData / usePlantDetail.
//
// Writes while offline split into two buckets:
//   • QUEUEABLE (edit / archive / delete / bulk-update / bulk-delete) —
//     recorded in the offline queue, optimistically applied to the
//     mirror, and resolved with a synthetic success so the caller's
//     happy path runs. They replay on reconnect (see useOfflineQueue).
//   • EVERYTHING ELSE (add-new plant, photo uploads, care-log, merge…) —
//     still blocked with a friendly toast; these need live Drive writes.
import axios from 'axios';
import { toast } from 'sonner';
import { enqueueMutation } from './offlineQueue';
import { applyMutationToMirror } from './offlineSnapshot';
import translations from '../i18n/translations';

// Cookie-based auth: send the httpOnly session cookie on every same-origin
// request. (Harmless for same-origin; needed if the API ever moves cross-origin.)
axios.defaults.withCredentials = true;

// Read a non-httpOnly cookie (used for the double-submit CSRF token).
function readCookie(name) {
  try {
    const prefix = `${name}=`;
    const hit = document.cookie.split('; ').find((c) => c.startsWith(prefix));
    return hit ? decodeURIComponent(hit.slice(prefix.length)) : null;
  } catch {
    return null;
  }
}

// Drop any stale "Bearer null"/"Bearer undefined" header left over from the
// pre-cookie era, and attach the CSRF token on state-changing requests.
axios.interceptors.request.use((config) => {
  try {
    const h = config.headers || {};
    const auth = h.Authorization || h.authorization;
    if (typeof auth === 'string' && /^Bearer\s+(null|undefined)?$/i.test(auth.trim())) {
      delete h.Authorization;
      delete h.authorization;
    }
    const m = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(m)) {
      const csrf = readCookie('csrf_token');
      if (csrf) h['X-CSRF-Token'] = csrf;
    }
    config.headers = h;
  } catch { /* non-fatal */ }
  return config;
});


// Minimal non-React string lookup for the two toasts fired from this
// interceptor (which has no access to the React i18n context). Reads the
// active language from localStorage and falls back to English.
function tLocal(key, fallback) {
  try {
    const lang = localStorage.getItem('plant_app_language') || 'en';
    return (translations[lang] && translations[lang][key]) || (translations.en && translations.en[key]) || fallback;
  } catch {
    return fallback;
  }
}

// Classify an outgoing write request into a queueable mutation, or null
// if it must be blocked. `url` is the request URL (may be absolute or
// the same-origin relative form we use everywhere).
function classifyMutation(method, url, data) {
  const m = (method || 'get').toLowerCase();
  const path = (url || '').split('?')[0];

  const archive = path.match(/\/api\/plants\/([^/]+)\/archive$/);
  if (m === 'post' && archive) {
    return { op: 'archive', plantId: archive[1] };
  }
  // Adding a photo to an existing plant (photo-add / merge). Queueable so a
  // photo taken offline uploads automatically on reconnect.
  const addPhoto = path.match(/\/api\/plants\/([^/]+)\/photos$/);
  if (m === 'post' && addPhoto) {
    return { op: 'add-photo', plantId: addPhoto[1] };
  }
  if (m === 'post' && /\/api\/plants\/bulk-update$/.test(path)) {
    return { op: 'bulk-update', payload: data };
  }
  if (m === 'post' && /\/api\/plants\/bulk-delete$/.test(path)) {
    return { op: 'bulk-delete', ids: (data && data.plant_ids) || [], payload: data };
  }
  const single = path.match(/\/api\/plants\/([^/]+)$/);
  if (single && single[1] !== 'bulk-update' && single[1] !== 'bulk-delete') {
    if (m === 'delete') return { op: 'delete', plantId: single[1] };
    if (m === 'put') return { op: 'edit', plantId: single[1], payload: data };
  }
  return null;
}

function syntheticResponse(config, mutation) {
  let data = {};
  if (mutation.op === 'bulk-update') {
    data = { updated: (mutation.payload?.plant_ids || []).length, queued: true };
  } else if (mutation.op === 'bulk-delete') {
    data = { deleted: (mutation.ids || []).length, queued: true };
  } else {
    data = { queued: true };
  }
  return { data, status: 200, statusText: 'OK (queued offline)', headers: {}, config, __queued: true };
}

let _offlineToastCooldown = 0;
let _queuedToastCooldown = 0;

axios.interceptors.request.use(async (config) => {
  const method = (config.method || 'get').toLowerCase();
  const isWrite = method !== 'get' && method !== 'head' && method !== 'options';
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (!isWrite || !offline) return config;

  const mutation = classifyMutation(method, config.url, config.data);
  if (mutation) {
    // Queue it, optimistically patch the mirror, and resolve with a
    // synthetic success so the caller's UI updates as if it saved.
    const userKey = localStorage.getItem('plant_mirror_user_key') || 'anon';
    let affectedNames = [];
    let restore = [];
    try {
      const res = await applyMutationToMirror(userKey, mutation);
      affectedNames = res.affectedNames || [];
      restore = res.restore || [];
    } catch { /* non-fatal */ }
    await enqueueMutation({
      user_key: userKey,
      op: mutation.op,
      method,
      url: config.url,
      data: config.data ?? null,
      plant_id: mutation.plantId || null,
      ids: mutation.ids || null,
      names: affectedNames,
      restore,
      attempts: 0,
      count: mutation.op === 'bulk-update'
        ? (mutation.payload?.plant_ids || []).length
        : mutation.op === 'bulk-delete'
          ? (mutation.ids || []).length
          : 1,
    });
    const now = Date.now();
    if (now - _queuedToastCooldown > 2500) {
      _queuedToastCooldown = now;
      toast.success(tLocal('savedOfflineToast', 'Saved offline \u2014 will sync when you reconnect'));
    }
    return Promise.reject(syntheticResponse(config, mutation));
  }

  // Non-queueable write while offline → block with the standard toast.
  const now = Date.now();
  if (now - _offlineToastCooldown > 3000) {
    _offlineToastCooldown = now;
    toast.error(tLocal('offlineWriteBlocked', 'You\u2019re offline \u2014 reconnect to save changes.'));
  }
  return Promise.reject({
    code: 'ERR_OFFLINE',
    message: 'Offline: write blocked',
    config,
    isAxiosError: true,
  });
});

// Convert a queued write's synthetic "rejection" into a resolved
// response so callers see it as a normal success.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error && error.__queued) return Promise.resolve(error);
    return Promise.reject(error);
  },
);
